// src/pages/user/UserAutoEstimator.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, Save, Trash2, DollarSign, Percent, AlertCircle,
  CheckCircle2, Building2, Package, Loader, RefreshCw,
  Download, FileSpreadsheet, PieChart,
} from "lucide-react";
import FeatureGate from "../../components/FeatureGate";
import { injectUserStyles } from "./userStyles";

/* ── Shared styles ── */
const STYLE_ID = "estimator-styles";
function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .est-header-card {
      background:#fff; border:1px solid #e8ecf0; border-radius:10px;
      padding:20px 24px; margin-bottom:20px;
    }
    .est-tab-bar { display:inline-flex; background:#f3f4f6; border-radius:8px; padding:3px; gap:2px; margin-top:16px; }
    .est-tab-btn {
      padding:7px 18px; border-radius:6px; font-size:13px; font-weight:500;
      cursor:pointer; border:none; transition:all 160ms;
    }
    .est-tab-btn.active { background:#fff; color:#0c1118; box-shadow:0 1px 4px rgba(0,0,0,.1); }
    .est-tab-btn:not(.active) { background:transparent; color:#6b7280; }
    .est-tab-btn:not(.active):hover { color:#374151; }
    .est-engine-badge {
      display:inline-flex; align-items:center; gap:6px;
      padding:6px 12px; background:rgba(16,185,129,.1); border:1px solid rgba(16,185,129,.2);
      border-radius:7px; font-size:12.5px; font-weight:500; color:#065f46;
    }
    .est-group-card { background:#fff; border:1px solid #e8ecf0; border-radius:10px; overflow:hidden; margin-bottom:16px; }
    .est-group-head {
      background:#0c1118; padding:16px 20px;
      display:flex; align-items:center; justify-content:space-between;
    }
    .est-group-name { font-family:'DM Sans',sans-serif; font-size:1.3rem; letter-spacing:.06em; color:#fff; }
    .est-group-sub  { font-size:11px; color:rgba(255,255,255,.5); margin-top:2px; font-family:'DM Sans',sans-serif; letter-spacing:.06em; }
    .est-table { width:100%; border-collapse:collapse; font-size:13px; }
    .est-table thead tr { background:#f9fafb; border-bottom:1px solid #e8ecf0; }
    .est-table th { padding:9px 14px; text-align:left; font-family:'DM Sans',sans-serif; font-size:9.5px; letter-spacing:.04em; color:#9ca3af; white-space:nowrap; }
    .est-table td { padding:11px 14px; border-bottom:1px solid #f3f4f6; vertical-align:middle; }
    .est-table tbody tr:last-child td { border-bottom:none; }
    .est-table tbody tr:hover td { background:#fafafa; }
    .est-input {
      padding:6px 10px 6px 28px; border:1px solid #e2e8f0; border-radius:6px;
      font-size:13px; width:110px; outline:none; transition:border-color 150ms;
      font-family:'DM Sans',sans-serif;
    }
    .est-input:focus { border-color:#f9ab14; }
    .est-input-readonly { background:#f9fafb; color:#9ca3af; }
    .est-input-wrap { position:relative; }
    .est-input-icon { position:absolute; left:8px; top:50%; transform:translateY(-50%); color:#9ca3af; }
    .est-save-btn {
      padding:6px 12px; border-radius:6px; font-size:12px; cursor:pointer; border:none;
      display:inline-flex; align-items:center; gap:4px; transition:background 150ms;
    }
    .est-save-btn.active { background:#0c1118; color:#fff; }
    .est-save-btn.inactive { background:#f3f4f6; color:#9ca3af; cursor:default; }
    .est-del-btn {
      padding:6px 10px; border-radius:6px; font-size:12px; cursor:pointer; border:none;
      background:rgba(239,68,68,.1); color:#b91c1c; display:inline-flex; align-items:center;
      transition:background 150ms;
    }
    .est-del-btn:hover { background:rgba(239,68,68,.18); }
    .est-no-engine {
      background:#fff; border:1px solid #e8ecf0; border-radius:10px;
      padding:40px 32px;
    }
    .est-no-engine-title { font-family:'DM Sans',sans-serif; font-size:1.6rem; letter-spacing:.04em; color:#0c1118; margin-bottom:6px; }
    .est-no-engine-sub   { font-size:13px; color:#9ca3af; margin-bottom:20px; }
    .est-warn-box {
      display:flex; align-items:flex-start; gap:10px;
      padding:12px 16px; background:rgba(249,171,20,.08); border:1px solid rgba(249,171,20,.2);
      border-radius:8px; margin-bottom:16px;
    }
    .est-takeoff-card { background:#fff; border:1px solid #e8ecf0; border-radius:10px; padding:16px 20px; margin-bottom:16px; }
    .est-summary-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin-bottom:16px; }
    @media (max-width:767px) { .est-summary-grid { grid-template-columns:1fr; } }
    .est-summary-kpi { border-radius:10px; padding:16px 20px; position:relative; overflow:hidden; }
    .est-summary-val { font-family:'DM Sans',sans-serif; font-size:2rem; font-weight:700; line-height:1; color:#fff; letter-spacing:.02em; }
    .est-summary-lbl { font-family:'DM Sans',sans-serif; font-size:10px; letter-spacing:.04em; color:rgba(255,255,255,.6); margin-top:3px; }
    .est-takeoff-grid { display:grid; grid-template-columns:2fr 1fr; gap:16px; }
    @media (max-width:1023px) { .est-takeoff-grid { grid-template-columns:1fr; } }
    .est-toast {
      position:fixed; bottom:24px; right:24px; z-index:60;
      display:flex; align-items:center; gap:10px; padding:12px 18px;
      border-radius:10px; font-size:13.5px; font-weight:500; color:#fff;
      box-shadow:0 8px 24px rgba(0,0,0,.2); animation:estToastIn .25s ease;
    }
    .est-toast-success { background:#0c1118; }
    .est-toast-error   { background:#dc2626; }
    @keyframes estToastIn { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  `;
  document.head.appendChild(el);
}

/* ── Constants ── */
const BASE = process.env.REACT_APP_BACKEND_URL || "";
const TAKEOFF_GET = (pid) => `/api/estimate/takeoff?projectId=${encodeURIComponent(pid)}`;
const TAKEOFF_PDF = (pid) => `${BASE}/api/estimate/takeoff/export.pdf?projectId=${encodeURIComponent(pid)}`;
const TAKEOFF_CSV = (pid) => `${BASE}/api/estimate/takeoff/export.csv?projectId=${encodeURIComponent(pid)}`;

async function jfetch(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, { headers: { "Content-Type": "application/json" }, credentials: "include", ...opts });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

const LENGTH_CATS = new Set(["lumber", "post", "beam", "truss"]);

function currency(x) { return Number(x || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); }

/* ── Donut chart (preserved exactly) ── */
function Donut({ parts }) {
  const total = parts.reduce((s, p) => s + p.value, 0) || 1;
  let acc = 0;
  const R = 36, C = 2 * Math.PI * R;
  return (
    <svg viewBox="0 0 80 80" className="w-40 h-40">
      <circle r={R} cx="40" cy="40" fill="transparent" stroke="#e5e7eb" strokeWidth="8" />
      {parts.map((p, i) => {
        const dash = (p.value / total) * C;
        const gap  = C - dash;
        const seg  = (
          <circle key={i} r={R} cx="40" cy="40" fill="transparent" strokeWidth="8"
            strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-acc}
            style={{ stroke: `hsl(${(i * 53) % 360} 70% 50%)` }} />
        );
        acc += dash;
        return seg;
      })}
      <circle r={R - 14} cx="40" cy="40" fill="white" />
      <text x="40" y="35" textAnchor="middle" className="text-xs font-bold fill-slate-700">Total</text>
      <text x="40" y="48" textAnchor="middle" className="text-[10px] font-bold fill-slate-900">${currency(total)}</text>
    </svg>
  );
}

/* ── Row component (preserved exactly) ── */
function Row({ row, onSave, onDelete }) {
  const [retail,    setRetail]    = useState(row.cost?.retailFinalUnit ?? "");
  const [wholesale, setWholesale] = useState(row.cost?.wholesaleFinalUnit ?? "");
  const [note,      setNote]      = useState(row.cost?.note ?? "");
  const [hasChanges, setHasChanges] = useState(false);

  const derived = (() => {
    const ws = Number(wholesale) || 0, rt = Number(retail) || 0;
    return { pct: ws > 0 ? ((rt - ws) / ws) * 100 : 0, abs: rt - ws };
  })();

  useEffect(() => {
    setRetail(row.cost?.retailFinalUnit ?? "");
    setWholesale(row.cost?.wholesaleFinalUnit ?? "");
    setNote(row.cost?.note ?? "");
  }, [row.cost?.retailFinalUnit, row.cost?.wholesaleFinalUnit, row.cost?.markupPct, row.cost?.markupAbs, row.cost?.note]);

  useEffect(() => {
    setHasChanges(
      retail !== (row.cost?.retailFinalUnit ?? "") ||
      wholesale !== (row.cost?.wholesaleFinalUnit ?? "") ||
      note !== (row.cost?.note ?? "")
    );
  }, [retail, wholesale, note, row]);

  return (
    <tr>
      <td style={{ fontSize: "13px", color: "#374151" }}>{row.label}</td>
      <td style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "12px", color: "#6b7280" }}>{row.lengthIn ?? "—"}</td>
      <td>
        <div className="est-input-wrap">
          <DollarSign size={13} className="est-input-icon" />
          <input className="est-input" value={retail} onChange={(e) => setRetail(e.target.value)} placeholder="25.00" />
        </div>
      </td>
      <td>
        <div className="est-input-wrap">
          <DollarSign size={13} className="est-input-icon" />
          <input className="est-input" value={wholesale} onChange={(e) => setWholesale(e.target.value)} placeholder="20.00" />
        </div>
      </td>
      <td>
        <div className="est-input-wrap">
          <Percent size={13} className="est-input-icon" />
          <input className={`est-input est-input-readonly`} value={Number.isFinite(derived.pct) ? derived.pct.toFixed(2) : "0"} readOnly />
        </div>
      </td>
      <td>
        <div className="est-input-wrap">
          <DollarSign size={13} className="est-input-icon" />
          <input className="est-input est-input-readonly" value={Number.isFinite(derived.abs) ? derived.abs.toFixed(2) : "0"} readOnly />
        </div>
      </td>
      <td style={{ textAlign: "right" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px" }}>
          <button className={`est-save-btn ${hasChanges ? "active" : "inactive"}`} disabled={!hasChanges} onClick={() => onSave(row, { retail, wholesale, note })}>
            <Save size={13} />
          </button>
          {row.engineScoped && onDelete && (
            <button className="est-del-btn" onClick={() => onDelete(row)}><Trash2 size={13} /></button>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ── Main component ── */
export default function UserAutoEstimator() {
  useEffect(() => { injectLocal(); }, []);

  const [tab,          setTab]          = useState("engine");
  const [hasEngine,    setHasEngine]    = useState(false);
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [toast,        setToast]        = useState(null);
  const [projects,     setProjects]     = useState([]);
  const [selectedPid,  setSelectedPid]  = useState("");
  const [takeoff,      setTakeoff]      = useState(null);
  const [loadingTakeoff, setLoadingTakeoff] = useState(false);

  async function loadEngineAndCosts() {
    try {
      setLoading(true);
      try {
        const r = await jfetch(`/api/pricing/user/engine/costs`);
        setItems(r.items || []); setHasEngine(true);
      } catch { setHasEngine(false); setItems([]); }
    } finally { setLoading(false); }
  }

  async function loadProjects() {
    try {
      const r = await jfetch(`/api/projects`);
      const list = (r.items || []).map((p) => ({ id: p.id, name: p.name || p.id }));
      list.sort((a, b) => a.name.localeCompare(b.name));
      setProjects(list);
      setSelectedPid((prev) => prev || list[0]?.id || "");
    } catch { setProjects([]); setSelectedPid(""); }
  }

  async function loadTakeoff(pid) {
    if (!pid) { setTakeoff(null); return; }
    setLoadingTakeoff(true);
    try {
      const r = await jfetch(TAKEOFF_GET(pid));
      const its = r.items || [];
      const subtotal = r.subtotal ?? its.reduce((s, it) => s + Number(it.qty || 0) * Number(it.unitPrice || 0), 0);
      setTakeoff({ items: its, subtotal, finalTotal: r.total ?? subtotal });
    } catch { setTakeoff(null); }
    finally { setLoadingTakeoff(false); }
  }

  const parts = useMemo(() => {
    if (!takeoff?.items?.length) return [];
    if (takeoff.stats?.byCategory?.length) return takeoff.stats.byCategory;
    const map = new Map();
    for (const it of takeoff.items) {
      const k = it.category || "other";
      const lt = Number(it.lineTotal ?? Number(it.qty || 0) * Number(it.unitPrice || 0));
      map.set(k, (map.get(k) || 0) + lt);
    }
    return Array.from(map, ([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 6);
  }, [takeoff]);

  useEffect(() => { loadEngineAndCosts().catch(() => {}); }, []);
  useEffect(() => { if (tab === "takeoff") loadProjects().then(() => {}); }, [tab]);
  useEffect(() => {
    if (tab !== "takeoff") return;
    if (!selectedPid) { setTakeoff(null); return; }
    loadTakeoff(selectedPid).catch(() => {});
  }, [tab, selectedPid]);

  function showToast(message, type = "success") { setToast({ message, type }); setTimeout(() => setToast(null), 3000); }

  async function createEngine() {
    try { await jfetch(`/api/pricing/user/engine/create`, { method: "POST", body: JSON.stringify({ name: "My Pricing Engine" }) }); showToast("Pricing engine created"); await loadEngineAndCosts(); }
    catch { showToast("Failed to create engine", "error"); }
  }

  async function saveRow(row, values) {
    try { await jfetch(`/api/pricing/user/engine/set`, { method: "POST", body: JSON.stringify({ registryId: row.id, retail: values.retail, wholesale: values.wholesale, note: values.note }) }); showToast("Pricing saved"); await loadEngineAndCosts(); }
    catch { showToast("Failed to save pricing", "error"); }
  }

  async function addEngineLength(group) {
    let dimension = group.dimension && group.dimension !== "custom" ? group.dimension : "";
    if (!dimension) { const d = window.prompt("Enter a dimension label (e.g. 2x6, 6x6, std):", "std"); if (!d) return; dimension = d.trim(); }
    const lengthStr = window.prompt("Length (inches)?", "120");
    const lengthIn = Number(lengthStr);
    if (!Number.isFinite(lengthIn) || lengthIn <= 0) return;
    try { await jfetch(`/api/pricing/user/engine/variant/add`, { method: "POST", body: JSON.stringify({ category: group.category, dimension, unitCode: group.unitCode, lengthIn }) }); showToast("Variant added"); await loadEngineAndCosts(); }
    catch { showToast("Failed to add variant", "error"); }
  }

  async function deleteVariant(row) {
    if (!row.engineScoped) { showToast("Only engine-specific rows can be deleted", "error"); return; }
    if (!window.confirm("Delete this engine-specific length?")) return;
    try { await jfetch(`/api/pricing/user/engine/variant/${row.id}`, { method: "DELETE" }); showToast("Variant deleted"); await loadEngineAndCosts(); }
    catch { showToast("Failed to delete variant", "error"); }
  }

  const groups = useMemo(() => {
    const map = new Map();
    for (const r of items) {
      if (!LENGTH_CATS.has(r.category)) continue;
      const key = `${r.category}__${r.dimension || "custom"}__${r.unitCode}`;
      if (!map.has(key)) map.set(key, { category: r.category, dimension: r.dimension || "custom", unitCode: r.unitCode, variants: [] });
      if (r.lengthIn != null) map.get(key).variants.push(r);
    }
    const arr = [...map.values()];
    arr.forEach((g) => g.variants.sort((a, b) => (a.lengthIn || 0) - (b.lengthIn || 0)));
    return arr;
  }, [items]);

  const others = useMemo(() => items.filter((r) => !LENGTH_CATS.has(r.category)), [items]);

  return (
    <FeatureGate required={["feat.estimator"]} requireActiveSub={true}
      title="Auto Estimator is not available on your current plan"
      intro="Upgrade to Expert to create and customize your personal pricing engine.">
      <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
          <div>
            <div className="up-title">Auto Estimator</div>
            <div className="up-sub">Manage your Pricing Engine or run a Takeoff for your projects</div>
          </div>
          {hasEngine && (
            <div className="est-engine-badge"><CheckCircle2 size={14} /> Engine Ready</div>
          )}
        </div>

        {/* Tab bar */}
        <div className="est-tab-bar" style={{ marginBottom: "20px" }}>
          <button className={`est-tab-btn ${tab === "engine" ? "active" : ""}`} onClick={() => setTab("engine")}>Pricing Engine</button>
          <button className={`est-tab-btn ${tab === "takeoff" ? "active" : ""}`} onClick={() => setTab("takeoff")}>Takeoff</button>
        </div>

        {/* ── ENGINE TAB ── */}
        {tab === "engine" && (
          <>
            {!hasEngine ? (
              <div className="est-no-engine">
                <div className="est-no-engine-title">My Pricing Engine</div>
                <div className="est-no-engine-sub">The basic data for the Pricing Engine is not ready. Contact your administrator or use the button below to create one.</div>
                <button className="up-btn up-btn-dark" onClick={createEngine}><Plus size={14} /> Create Engine</button>
              </div>
            ) : (
              <>
                {loading && <div className="up-loading"><div className="up-spinner" /></div>}

                {groups.map((g) => (
                  <div className="est-group-card" key={`${g.category}-${g.dimension}-${g.unitCode}`}>
                    <div className="est-group-head">
                      <div>
                        <div className="est-group-name">{g.category}</div>
                        <div className="est-group-sub">{g.dimension} · Unit: {g.unitCode}</div>
                      </div>
                      <button className="up-btn up-btn-amber" style={{ padding: "7px 14px", fontSize: "12.5px" }} onClick={() => addEngineLength(g)}>
                        <Plus size={13} /> Add Variant
                      </button>
                    </div>
                    <div style={{ padding: "16px 20px" }}>
                      {!g.variants.length ? (
                        <div className="est-warn-box">
                          <AlertCircle size={15} style={{ color: "#f9ab14", flexShrink: 0, marginTop: 1 }} />
                          <div>
                            <div style={{ fontSize: "13px", fontWeight: 600, color: "#92400e" }}>No variants configured</div>
                            <div style={{ fontSize: "12px", color: "#78350f", marginTop: "3px" }}>Click "Add Variant" to configure at least one length for this material.</div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ overflowX: "auto", borderRadius: "7px", border: "1px solid #e8ecf0" }}>
                          <table className="est-table">
                            <thead><tr>
                              <th>Label</th><th>Length (in)</th><th>Retail</th>
                              <th>Wholesale</th><th>Markup %</th><th>Markup $</th>
                              <th style={{ textAlign: "right" }}>Actions</th>
                            </tr></thead>
                            <tbody>{g.variants.map((r) => <Row key={r.id} row={r} onSave={saveRow} onDelete={deleteVariant} />)}</tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {others.length > 0 && (
                  <div className="est-group-card">
                    <div className="est-group-head">
                      <div>
                        <div className="est-group-name">Other Materials</div>
                        <div className="est-group-sub">Non-length-based products</div>
                      </div>
                    </div>
                    <div style={{ padding: "16px 20px" }}>
                      <div style={{ overflowX: "auto", borderRadius: "7px", border: "1px solid #e8ecf0" }}>
                        <table className="est-table">
                          <thead><tr>
                            <th>Category</th><th>Label</th><th>Retail</th>
                            <th>Wholesale</th><th>Markup %</th><th>Markup $</th>
                            <th style={{ textAlign: "right" }}>Actions</th>
                          </tr></thead>
                          <tbody>{others.map((r) => <Row key={r.id} row={r} onSave={saveRow} />)}</tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── TAKEOFF TAB ── */}
        {tab === "takeoff" && (
          <>
            {/* Project selector */}
            <div className="est-takeoff-card">
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px" }}>
                <Package size={16} style={{ color: "#10b981", flexShrink: 0 }} />
                <label style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "10px", letterSpacing: ".04em", color: "#6b7280" }}>Select Project</label>
                <select
                  style={{ flex: 1, minWidth: 200, padding: "8px 12px", border: "1px solid #e2e8f0", borderRadius: "7px", fontSize: "13px", outline: "none", fontFamily: "'DM Sans',sans-serif" }}
                  value={selectedPid} onChange={(e) => setSelectedPid(e.target.value)}
                >
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  {!projects.length && <option value="">No projects available</option>}
                </select>
                <button className="up-btn up-btn-dark" onClick={() => loadTakeoff(selectedPid)} disabled={!selectedPid || loadingTakeoff}>
                  <RefreshCw size={13} style={{ animation: loadingTakeoff ? "upSpin .7s linear infinite" : "none" }} /> Refresh
                </button>
              </div>
            </div>

            {loadingTakeoff ? (
              <div className="up-loading" style={{ minHeight: "200px" }}><Loader size={24} style={{ animation: "upSpin .7s linear infinite", color: "#10b981" }} /><span>Loading takeoff…</span></div>
            ) : !selectedPid ? (
              <div className="up-empty"><Package size={40} style={{ color: "#d1d5db", margin: "0 auto" }} /><div className="up-empty-sub">Select a project to view the takeoff</div></div>
            ) : !takeoff ? (
              <div className="up-empty"><AlertCircle size={40} style={{ color: "#f9ab14", margin: "0 auto" }} /><div className="up-empty-title">No takeoff available</div><div className="up-empty-sub">This project doesn't have a takeoff yet</div></div>
            ) : (
              <>
                {/* Summary KPIs */}
                <div className="est-summary-grid">
                  {[
                    { bg: "#0c1118", val: takeoff.items?.length || 0, lbl: "Total Items" },
                    { bg: "#1d9e75", val: `$${currency(takeoff.subtotal)}`, lbl: "Subtotal" },
                    { bg: "#6366f1", val: `$${currency(takeoff.finalTotal)}`, lbl: "Final Total" },
                  ].map((k) => (
                    <div key={k.lbl} className="est-summary-kpi" style={{ background: k.bg }}>
                      <div className="est-summary-val">{k.val}</div>
                      <div className="est-summary-lbl">{k.lbl}</div>
                    </div>
                  ))}
                </div>

                <div className="est-takeoff-grid">
                  {/* Materials table */}
                  <div className="up-panel">
                    <div className="up-panel-head">
                      <div className="up-panel-title">Materials &amp; Costs</div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <a className="up-btn up-btn-dark" style={{ padding: "5px 12px", fontSize: "12px", textDecoration: "none" }} href={TAKEOFF_PDF(selectedPid)} download>
                          <Download size={12} /> PDF
                        </a>
                        <a className="up-btn up-btn-outline" style={{ padding: "5px 12px", fontSize: "12px", textDecoration: "none" }} href={TAKEOFF_CSV(selectedPid)} download>
                          <FileSpreadsheet size={12} /> CSV
                        </a>
                      </div>
                    </div>
                    <div style={{ overflowX: "auto", maxHeight: 560 }}>
                      <table className="est-table">
                        <thead style={{ position: "sticky", top: 0 }}>
                          <tr><th>Component</th><th style={{ textAlign: "right" }}>Qty</th><th>Unit</th><th style={{ textAlign: "right" }}>Price</th><th style={{ textAlign: "right" }}>Total</th></tr>
                        </thead>
                        <tbody>
                          {(takeoff.items || []).map((it) => (
                            <tr key={it.id || it.key}>
                              <td>
                                <div style={{ fontWeight: 500, color: "#0c1118", fontSize: "13px" }}>{it.label || it.key}</div>
                                {it.category && <div style={{ fontSize: "11px", color: "#9ca3af", textTransform: "capitalize" }}>{it.category}</div>}
                              </td>
                              <td style={{ textAlign: "right", fontWeight: 600, fontFamily: "'DM Sans', sans-serif", fontSize: "12px" }}>{Number(it.qty).toLocaleString()}</td>
                              <td style={{ color: "#6b7280", fontSize: "12px" }}>{it.unit}</td>
                              <td style={{ textAlign: "right", fontFamily: "'DM Sans', sans-serif", fontSize: "12px" }}>${currency(it.unitPrice)}</td>
                              <td style={{ textAlign: "right", fontWeight: 700, fontFamily: "'DM Sans', sans-serif", fontSize: "12px" }}>${currency(it.lineTotal ?? Number(it.qty || 0) * Number(it.unitPrice || 0))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Donut chart */}
                  <div className="up-panel" style={{ padding: "20px" }}>
                    <div className="up-panel-title" style={{ marginBottom: "16px" }}>
                      <PieChart size={15} style={{ color: "#f9ab14" }} /> Cost Breakdown
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
                      <Donut parts={parts} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      {parts.map((p, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: "#f9fafb", borderRadius: "6px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div style={{ width: 10, height: 10, borderRadius: "50%", background: `hsl(${(i * 53) % 360} 70% 50%)` }} />
                            <span style={{ fontSize: "12.5px", color: "#374151", textTransform: "capitalize" }}>{p.label}</span>
                          </div>
                          <span style={{ fontSize: "12.5px", fontWeight: 700, fontFamily: "'DM Sans', sans-serif", color: "#0c1118" }}>${currency(p.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Toast */}
        {toast && (
          <div className={`est-toast ${toast.type === "success" ? "est-toast-success" : "est-toast-error"}`}>
            {toast.type === "success" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {toast.message}
          </div>
        )}
      </div>
    </FeatureGate>
  );
}
